import { 
    AppBase, 
    BLEND_NONE, 
    BoundingBox, 
    Color, 
    Entity, 
    RaycastResult, 
    StandardMaterial, 
    Vec3, 
    Vec2,
    Quat,
    math,
    BLEND_NORMAL,
    Layer,
    SORTMODE_NONE,
    TranslateGizmo,
    RotateGizmo,
    ScaleGizmo,
    Mat3,
    Mat4
 } from "playcanvas";


// Define interfaces to extend the Document and Element
interface RulerMarkingDocument extends Document {
    mozPointerLockElement?: Element | null;
    webkitPointerLockElement?: Element | null;
    mozExitPointerLock?: () => void;
    webkitExitPointerLock?: () => void;
}

// Define interfaces to extend the Element
interface RulerMarkingElement extends HTMLElement {
    mozRequestPointerLock?: () => void;
    webkitRequestPointerLock?: () => void;
}

// Define the required listeners for the pointer lock
declare function onMouseMove(e: MouseEvent): void;
declare function onMouseUp(e: MouseEvent): void;

const DrawOrderBasic = 1000;
let DrawOrder = 1000;
let LastDrawOrderOffset = 1; 

class LabelInput {

    // utilities
    private App: AppBase;
    private CameraEntity: Entity;

    // position in world space of engine
    private WorldPosition: Vec3;

    // for enable/disable update
    private isEnabled: boolean = true;

    // input field of label
    public InputField: HTMLInputElement;

    constructor(app: AppBase, camera_entity: Entity){
        
        // set utilities
        this.App = app;
        this.CameraEntity = camera_entity;
        this.WorldPosition = new Vec3();

        // create input field
        this.InputField = document.createElement('input');
        this.InputField.type = 'text';
        this.InputField.className = "rm-input-label";

        // avoid mouse events penetrate the input field 
        ['mousedown', 'mousemove', 'mouseup', 'click', 'touchstart', 'touchend'].forEach(event => {
             this.InputField.addEventListener(event, e => e.stopPropagation());
        });

        // append to the document
        document.body.appendChild(this.InputField);

        // register the update function to app of the engine
        this.App.on('update', this.Update, this);
    }

    private Update(): void {

        // update the position of the input field based on the camera of the engine
        if (!this.isEnabled) return;
        const screenPos = new Vec3();
        this.CameraEntity.camera.worldToScreen(this.WorldPosition, screenPos);
        this.InputField.style.left = `${screenPos.x}px`;
        this.InputField.style.top = `${screenPos.y}px`;

    }

    public Enable(): void {

        // set enable and show input field
        this.isEnabled = true;
        this.InputField.style.display = 'block';

    }

    public Disable(): void {

        // set disable and hide input field
        this.isEnabled = false;
        this.InputField.style.display = 'none';

    }


    public SetPosition(position: Vec3): void {

        // set world position
        this.WorldPosition.copy(position);

    }

    public SetPlaceHolder(text: string): void {

        // set the text of place holder
        this.InputField.placeholder = text;

    }

    public Destroy(): void {

        // delete the input field
        this.isEnabled = false;
        this.InputField.remove();

        // unregister register the update function from app of the engine
        this.App.off('update', this.Update, this);
    }
}


// === [class of components of the marking line] ===
class LineComponent{

    // utilities
    private App: AppBase;
    private EntityScale: number;

    // positions of end points
    public StartPosition: Vec3;
    public EndPosition: Vec3;
    
    // entities and label
    public StartSphereEntity: Entity;
    public EndSphereEntity: Entity;
    public LineCylinderEntity: Entity;
    public LineLabelEntity: LabelInput;
    public MapEntityToPosition: Map<Entity, Vec3>;

    // check for line done or not
    public isLineDone: boolean;
    
    constructor(start_position: Vec3, end_position: Vec3, app: AppBase, entity_scale:number){

        // start and end positions in world space
        this.StartPosition = start_position;
        this.EndPosition = end_position;

        // set app
        this.App = app;

        // map from entity to start/end sphere
        this.MapEntityToPosition = new Map();

        // set Scale of object
        this.EntityScale = entity_scale;

        // sphere for start and end positions
        this.StartSphereEntity = null;
        if (this.StartPosition !== null){
            this.StartSphereEntity = this.CreateSceneSphere(this.StartPosition);
            this.MapEntityToPosition.set(this.StartSphereEntity, this.StartPosition);
        }
        this.EndSphereEntity = null;
        if (this.EndPosition !== null){
            this.EndSphereEntity = this.CreateSceneSphere(this.EndPosition)
            this.MapEntityToPosition.set(this.EndSphereEntity, this.EndPosition);
        }

        // create line line if both the start and end positions are given
        this.LineLabelEntity = null; 
        this.LineCylinderEntity = null;

        this.isLineDone = false;
    }

    SetColor(start_color: Color, line_color: Color, end_color: Color){
        if (this.StartSphereEntity){
            const material = this.StartSphereEntity.model.material as StandardMaterial;
            material.diffuse = start_color;
            material.emissive = start_color;
            material.blendType = BLEND_NONE;
            material.depthTest = true;
            material.depthWrite = true;
            material.update();
        } 
        if (this.LineCylinderEntity) {
            const material = this.LineCylinderEntity.model.material as StandardMaterial;
            material.diffuse = line_color;
            material.emissive = line_color;
            material.blendType = BLEND_NONE;
            material.depthTest = true;
            material.depthWrite = true;
            material.update();
        }
        if (this.EndSphereEntity){
            const material = this.EndSphereEntity.model.material as StandardMaterial;
            material.diffuse = end_color;
            material.emissive = end_color;
            material.blendType = BLEND_NONE;
            material.depthTest = true;
            material.depthWrite = true;
            material.update();
        } 
    }

    SetScale(entity_scale: number){

        this.EntityScale = entity_scale;

        if (this.StartSphereEntity){
            this.StartSphereEntity.setLocalScale(this.EntityScale, this.EntityScale, this.EntityScale);
            this.StartSphereEntity.collision.radius = this.EntityScale * 0.55;
        } 
        if (this.LineCylinderEntity) {
            const origin_scale = this.LineCylinderEntity.getScale();
            this.LineCylinderEntity.setLocalScale(this.EntityScale / 3, origin_scale.y, this.EntityScale / 3);
            this.UpdateLineObject(this.LineCylinderEntity, this.StartPosition, this.EndPosition);
        }
        if (this.EndSphereEntity){
            this.EndSphereEntity.setLocalScale(this.EntityScale, this.EntityScale, this.EntityScale);
            this.EndSphereEntity.collision.radius = this.EntityScale * 0.55;
        } 
    }

    CreateSceneSphere(position: Vec3, color = 0xFFFFFF) {

        const sphere_entity = new Entity('sphere');
        this.App.root.addChild(sphere_entity);

        sphere_entity.tags.add('endpoint');

        sphere_entity.setPosition(position);
        sphere_entity.setLocalScale(this.EntityScale, this.EntityScale, this.EntityScale);

        sphere_entity.addComponent('model', { type: 'sphere' });

        const material = new StandardMaterial();
        material.diffuse = new Color(1,1,1);
        material.emissive = new Color(1,1,1);
        material.blendType = BLEND_NONE;
        material.depthTest = true;
        material.depthWrite = true;
        material.update();

        sphere_entity.model.material = material;

        sphere_entity.model.meshInstances.forEach(mi => mi.drawOrder = DrawOrder);
        DrawOrder -= 1;

        sphere_entity.addComponent('rigidbody', { type: 'kinematic' });
        sphere_entity.addComponent('collision', { type: 'sphere'});
        sphere_entity.collision.radius = this.EntityScale * 0.55;

        sphere_entity.enabled = true;

        this.App.renderNextFrame = true;



        return sphere_entity;
    }

    CreateDrawLine(){
        if (this.StartPosition !== null && this.EndPosition !== null) {
            this.LineCylinderEntity = this.CreateLineWithLabel(this.StartPosition, this.EndPosition);
            this.isLineDone = true;
        }
    }

    

    //#region [Add/Remove Sceme Objects]
    AddObjectsToScene(){
        // add objects to THREE scene
        if(this.StartSphereEntity) this.StartSphereEntity.enabled = true;
        if(this.EndSphereEntity) this.EndSphereEntity.enabled = true;
        if(this.LineCylinderEntity) this.LineCylinderEntity.enabled = true;
        if(this.LineLabelEntity)  this.LineLabelEntity.Enable();
    }

    RemoveObjectsFromScene(){
        // remove objects from THREE scene
        if(this.StartSphereEntity) this.StartSphereEntity.enabled = false;
        if(this.EndSphereEntity) this.EndSphereEntity.enabled = false;
        if(this.LineCylinderEntity) this.LineCylinderEntity.enabled = false;
        if(this.LineLabelEntity)  this.LineLabelEntity.Disable();
    }
    //#endregion
    
    UpdateInputLabel(position: Vec3, str_rounded_length: string, str_length: string){
        if(this.LineLabelEntity){
            this.LineLabelEntity.SetPosition(position);
            this.LineLabelEntity.SetPlaceHolder(str_rounded_length);
            this.LineLabelEntity.InputField.setAttribute('holded-value', str_length);
        }
    }

    UpdateDrawLine(){

        // update spheres
        if(this.StartSphereEntity) this.StartSphereEntity.setPosition(this.StartPosition);
        if(this.EndSphereEntity) this.EndSphereEntity.setPosition(this.EndPosition);

        // update label
        if(this.LineLabelEntity){
            const midpoint = new Vec3().add2(this.StartPosition, this.EndPosition).mulScalar(0.5);
            this.LineLabelEntity.SetPosition(midpoint);
        }

        // udpate line
        this.UpdateLineObject(this.LineCylinderEntity, this.StartPosition, this.EndPosition);

        this.App.renderNextFrame = true;
    }

    IsDrawLineDone(){
        return this.isLineDone;
    }

    SetEndPosition(position: Vec3){

        // set end position and end sphere
        this.EndPosition = position;
        // if (this.EndPosition) this.ConstrainVectorPrecisions(this.EndPosition);
        this.EndSphereEntity = (this.EndPosition !== null) ? this.CreateSceneSphere(this.EndPosition) : null;
        this.MapEntityToPosition.set(this.EndSphereEntity, this.EndPosition);
    }

    SetEndPositionByLength(target_length: number){

        if (!this.isLineDone) return;

        // Calculate the direction vector from the start to the current end position.
        const direction = new Vec3().sub2(this.EndPosition, this.StartPosition);

        // Normalize the direction vector (make its length 1) and then scale it
        // by the target length.
        direction.normalize().mulScalar(target_length);

        // Calculate the new end position by adding the scaled direction to the start position.
        const new_end_position = new Vec3().add2(this.StartPosition, direction);

        // Update the instance's EndPosition with the new values.
        this.EndPosition.copy(new_end_position);
    }

    CreateLineWithLabel(start_position: Vec3, end_position: Vec3, color: Color = new Color(1, 1, 1)) {

        // create the cylinder for line
        const cylinder_entity = new Entity('line-cylinder');
        this.App.root.addChild(cylinder_entity);
        cylinder_entity.tags.add('endpoint');

        cylinder_entity.addComponent('model', { type: 'cylinder' });

        const material = new StandardMaterial();
        material.diffuse = new Color(1,1,1);
        material.emissive = new Color(1,1,1);
        material.blendType = BLEND_NONE;
        material.depthTest = true;
        material.depthWrite = true;
        material.update();
        cylinder_entity.model.material = material;
       
        cylinder_entity.addComponent('rigidbody', { type: 'kinematic' });
        cylinder_entity.addComponent('collision', { type: 'cylinder', axis: 1 });

        // set initial position and scale
        cylinder_entity.setPosition(start_position);
        cylinder_entity.setLocalScale(this.EntityScale / 3, 0.5, this.EntityScale / 3);

        cylinder_entity.model.meshInstances.forEach(mi => mi.drawOrder = DrawOrder);
        DrawOrder -= 1;

        // update the line's position, rotation, and scale
        this.UpdateLineObject(cylinder_entity, start_position, end_position);

        this.App.renderNextFrame = true;
        
        return cylinder_entity
    }

    UpdateLineObject(line_entity: Entity, start_position: Vec3, end_position: Vec3) {
        
        const direction = new Vec3().sub2(end_position, start_position);
        const height = direction.length();
        const midpoint = new Vec3().add2(start_position, end_position).mulScalar(0.5);

        const radius = line_entity.getLocalScale().x; 
        line_entity.setLocalScale(radius, height, radius);

        line_entity.setPosition(midpoint);

        const up = new Vec3(0, 1, 0);
        const dirNorm = direction.clone().normalize();

        const axis = new Vec3().cross(up, dirNorm).normalize();
        const angle = Math.acos(up.dot(dirNorm));
        const rotation = new Quat().setFromAxisAngle(axis, math.RAD_TO_DEG * angle);
        line_entity.setLocalRotation(rotation);

        if (line_entity.collision) {
            line_entity.collision.height = height * 0.9;
            line_entity.collision.radius = radius;
        }
    }

    Destroy(){

        this.App.root.removeChild(this.StartSphereEntity);
        this.App.root.removeChild(this.EndSphereEntity);
        this.App.root.removeChild(this.LineCylinderEntity);
        if(this.LineLabelEntity) this.LineLabelEntity.Destroy();
        this.MapEntityToPosition.clear();

        this.App.renderNextFrame = true;
    }

    GetLength(){
        return (this.StartPosition.distance(this.EndPosition));
    }

    GetMidPoint(){
        return new Vec3().add2(this.StartPosition, this.EndPosition).mulScalar(0.5);
    }
}


class RulerMarking{

    App: AppBase;

    EntityScale: number;

    SplatBoundingBox: BoundingBox;
    
    DictUnitFactors: Map<string, number>;
    
    UnitMode: number;

    // IsReScale: boolean;
    IsReScale: boolean;

    IsEnableRulerMarking: boolean;
    IsEnableRulerMarkingEdit: boolean;

    ScaleFactor: number;
    
    MarkingLines: LineComponent[];

    MapSphereToDrawLine : Map<Entity, LineComponent>;
    MapCylinderToDrawLine : Map<Entity, LineComponent>;
    MapDrawLineToUIObject : Map<Entity, LineComponent>;
    MapUIObjectToDrawLine : Map<Entity, LineComponent>;

    // use old mode
    EnableOldMode: boolean;

    // selected line
    CurrentSelectedDrawLine : LineComponent;
    ColorDefault: Color;
    ColorSelectedLine: Color;
    ColorSelectedPoint: Color;

    // edit line
    CurrentSelectedSphere : Entity;

    DrawLineCube : Entity;
    IsDrawOnReferenceEntity : boolean;

    UIToggle : boolean;
    IsPointerLocked : boolean;
    IsDraggingActive : boolean;

    MouseDisplaceCriteria : number;
    Sensitivity : number;

    UIContainer : HTMLDivElement;
    UIDrawLinesContainer : HTMLDivElement;
    UIScaleInput : HTMLInputElement;

    Ammo: any|null;

    isDragging: boolean;


    GizmoTranslate: TranslateGizmo;
    GizmoIsDragging: boolean;

    GizmoTranslateRef: TranslateGizmo;
    GizmoScaleRef: ScaleGizmo;
    GizmoRotateRef: RotateGizmo;

    GetCameraInfo: () => string;

    EulerAngle: Vec3;
    GSplatEntityPosition: Vec3;
    SpaceOuat: Quat;

    constructor(
        app: AppBase, 
        splat_bounding_box: BoundingBox,
        get_camera_info: () => string,
        eulerangle: Vec3,
        gsplat_entity_pos: Vec3, // [Future Work] remove gsplat position and eulerangle of old drawing URL
        quat: Quat, 
        color_default: Color = Color.WHITE, 
        color_selected_line: Color = Color.CYAN, 
        color_selected_point: Color = Color.RED
    ){

        // get euler angle and position of main gsplat
        this.EulerAngle = eulerangle;

        this.GSplatEntityPosition = gsplat_entity_pos;

        this.SpaceOuat = quat;

        // set app
        this.App = app;

        // set boudning box
        this.SplatBoundingBox = splat_bounding_box;

        // callback for camera information
        this.GetCameraInfo = get_camera_info;

        this.EnableOldMode = false;
        
        // set the scale for the entity
        this.EntityScale = 0.03 * Math.max(this.SplatBoundingBox.halfExtents.x, this.SplatBoundingBox.halfExtents.y, this.SplatBoundingBox.halfExtents.z);
        
        // units
        this.DictUnitFactors = new Map<string, number>([
            ["mm", 0.1],
            ["cm", 1],
            ["m", 100],
            ["km", 100000]
        ]);
            
        // unit mode (0: auto, 1: mm, 2: cm, 3: m, 4: km)
        this.UnitMode = 0;

        // rescale mode
        this.IsReScale = true;
        
        // enable/disable ruler marking
        this.IsEnableRulerMarking = false;
        this.IsEnableRulerMarkingEdit = false;

        // list of lines
        this.MarkingLines = [];

        // scale factor for length
        this.ScaleFactor = 1;

        // dictionary maps object to line
        this.MapSphereToDrawLine = new Map();
        this.MapCylinderToDrawLine = new Map();
        this.MapDrawLineToUIObject = new Map();
        this.MapUIObjectToDrawLine = new Map();

        // selected line
        this.CurrentSelectedDrawLine = null;
        this.ColorDefault = color_default;
        this.ColorSelectedLine = color_selected_line;
        this.ColorSelectedPoint = color_selected_point;
    
        // edit line
        this.CurrentSelectedSphere = null;
        
        // draw line helper
        this.DrawLineCube = this.CreateReferenceBox(this.SplatBoundingBox.center, this.SplatBoundingBox.halfExtents, Color.BLUE);
        this.IsDrawOnReferenceEntity = true;

        // setup UI items
        this.UIToggle = false;
        this.IsPointerLocked = false;
        this.IsDraggingActive = false;

        this.MouseDisplaceCriteria = 10;
        this.Sensitivity = 0.1;

        const uis = this.UI_Initialization();
        this.UIContainer = uis.ui_container;
        this.UIDrawLinesContainer = uis.drawlines_container;

        document.addEventListener('pointerlockchange', this.PointerLockChangeHandler.bind(this), false);
        document.addEventListener('mozpointerlockchange', this.PointerLockChangeHandler.bind(this), false);
        document.addEventListener('webkitpointerlockchange', this.PointerLockChangeHandler.bind(this), false);

        // get initial lines and scale factor
        this.GetDrawLinesFromURL();
        this.UI_CreateLines();
        this.DisableDrawLines();

        // gizmo
        const gizmo_layer = new Layer({
            name: 'Gizmo',
            clearDepthBuffer: true,
            opaqueSortMode: SORTMODE_NONE,
            transparentSortMode: SORTMODE_NONE
        });
        const layers = this.App.scene.layers;
        layers.push(gizmo_layer);
        const camera_entity = this.App.root.findByName('camera') as Entity;
        camera_entity.camera.layers = camera_entity.camera.layers.concat(gizmo_layer.id);

        // create transform for reference cube dragging, rotating and scaling
        this.GizmoTranslateRef = new TranslateGizmo(camera_entity.camera, gizmo_layer);
        this.GizmoTranslateRef.on('pointer:down', (_x, _y, meshInstance) => { this.App.fire('gizmo:pointer', !!meshInstance); });
        this.GizmoTranslateRef.on('pointer:up', (_x, _y, meshInstance) => { this.App.fire('gizmo:pointer', false); this.App.renderNextFrame = true;});

        this.GizmoRotateRef = new RotateGizmo(camera_entity.camera, gizmo_layer);
        this.GizmoRotateRef.on('pointer:down', (_x, _y, meshInstance) => { this.App.fire('gizmo:pointer', !!meshInstance);});
        this.GizmoRotateRef.on('pointer:up', (_x, _y, meshInstance) => { this.App.fire('gizmo:pointer', false); this.App.renderNextFrame = true;});

        this.GizmoScaleRef = new ScaleGizmo(camera_entity.camera, gizmo_layer);
        this.GizmoScaleRef.on('pointer:down', (_x, _y, meshInstance) => { 
            this.GizmoIsDragging = true;
            this.App.fire('gizmo:pointer', !!meshInstance);
        });
        // box_entity.collision.halfExtents = half_extents.clone().mulScalar(0.5);
        this.GizmoScaleRef.on('pointer:move', (_x, _y, meshInstance) => {
            if(this.DrawLineCube && this.GizmoIsDragging){
                this.DrawLineCube.collision.halfExtents = this.DrawLineCube.getScale().clone().mulScalar(0.5);
                this.App.renderNextFrame = true;
            }
        });
        this.GizmoScaleRef.on('pointer:up', (_x, _y, meshInstance) => { 
            this.GizmoIsDragging = false;
            this.App.fire('gizmo:pointer', false); 
            this.App.renderNextFrame = true;
        });

        // create dragging gizmo for endpoints 
        this.GizmoIsDragging = false;
        this.GizmoTranslate = new TranslateGizmo(camera_entity.camera, gizmo_layer);
        this.GizmoTranslate.on('pointer:down', (_x, _y, meshInstance) => {
            this.GizmoIsDragging = true;
            this.App.fire('gizmo:pointer', !!meshInstance);
        });
        this.GizmoTranslate.on('pointer:move', (_x, _y, meshInstance) => {
            if(this.CurrentSelectedSphere && this.GizmoIsDragging){
            //    const current_line = this.MapSphereToDrawLine.get(this.CurrentSelectedSphere);
            //    let pos = current_line.MapEntityToPosition.get(this.CurrentSelectedSphere);
            //    pos = this.CurrentSelectedSphere.getPosition().clone();
            //    current_line.UpdateDrawLine();
            //    this.UpdateLineLabel(current_line);
                const new_position = this.CurrentSelectedSphere.getPosition().clone();
                this.EditDrawLineVertex(this.MapSphereToDrawLine.get(this.CurrentSelectedSphere), this.CurrentSelectedSphere, new_position);
                this.UI_CreateLines();
            }
        });
        this.GizmoTranslate.on('pointer:up', (_x, _y, meshInstance) => {
            this.GizmoIsDragging = false;
            this.App.fire('gizmo:pointer', false);
            this.App.renderNextFrame = true;
        });
    }

    CreateReferenceBox(center_position: Vec3, half_extents: Vec3, color: Color, opacity: number = 0.5){

        const box_entity = new Entity('box');
        this.App.root.addChild(box_entity);
        box_entity.tags.add('drawref');

        box_entity.setPosition(center_position);
        box_entity.setLocalScale(half_extents.clone());

        box_entity.addComponent('model', { type: 'box' });

        const material = new StandardMaterial();
        material.blendType = BLEND_NORMAL;
        material.diffuse = color;
        material.emissive = color;
        material.opacity = opacity;

        material.depthTest = true;
        material.depthWrite = false;
        material.update();

        box_entity.model.material = material;

        box_entity.model.meshInstances.forEach(mi => mi.drawOrder = DrawOrder);
        DrawOrder -= 1;

        box_entity.addComponent('rigidbody', { type: 'kinematic' });
        box_entity.addComponent('collision', { type: 'box' });
        box_entity.collision.halfExtents = half_extents.clone().mulScalar(0.5);
        box_entity.enabled = true;

        this.App.renderNextFrame = true;

        return box_entity;
    }


    private UI_AddMultiSwitch(
        target_container: HTMLDivElement,
        title_label: string,
        labels: string[],
        functions: Array<() => void>,
    ): void {

        if (labels.length !== functions.length || labels.length === 0) {
            return;
        }

        // setup spacing and amout of options
        const spacing = 2.5;
        const num_options = labels.length;
        const slider_step = (100 - spacing * 2) / num_options;

        // create container
        const switch_container = document.createElement('div');
        switch_container.className = 'rm-bin-switch';
        target_container.appendChild(switch_container);

        // create title
        const title_span = document.createElement('span');
        title_span.className = 'rm-bin-switch-label';
        title_span.textContent = title_label;
        switch_container.appendChild(title_span);

        // create button
        const button_container = document.createElement('div');
        button_container.className = 'rm-bin-switch-button';
        switch_container.appendChild(button_container);

        // create slider
        const slider = document.createElement('div');
        slider.className = 'rm-bin-switch-slider';
        slider.style.width = `${slider_step}%`;
        button_container.appendChild(slider);

        // generate spans for options
        labels.forEach((label, index) => {

            const left_precent = spacing + slider_step * index;

            const span = document.createElement('span');
            span.className = `rm-bin-switch-span`;
            span.textContent = label;
            span.style.width = `${slider_step}%`;
            span.style.left = `${left_precent}%`;

            button_container.appendChild(span);

            span.addEventListener('click', () => {
                
                slider.style.left = `${left_precent}%`;
                functions[index]();
            });

        });
    }

    private DetachGizmos(){
        this.GizmoTranslate.detach();
        this.GizmoTranslateRef.detach();
        this.GizmoRotateRef.detach();
        this.GizmoScaleRef.detach();
    }

    private SimpleInputField(target_container: HTMLDivElement, label: string, func: (e: Event) => void, default_value: Number, min: Number = 0.01, max: Number = 100.0, step: Number = 0.01){

        // --- create numeric input
        const input_container = document.createElement('div');
        input_container.className = 'rm-input-field-container';
        target_container.appendChild(input_container);

        // create title
        const input_title_span = document.createElement('span');
        input_title_span.className = 'rm-input-field-label';
        input_title_span.textContent = label;
        input_container.appendChild(input_title_span);

        // create number input field
        const number_input = document.createElement('input');
        number_input.className = 'rm-input-field';
        number_input.type = 'number';
        number_input.value = default_value.toString();
        number_input.min = `${min}`;
        number_input.max = `${max}`;
        number_input.step = `${step}`;
        input_container.appendChild(number_input);

        number_input.addEventListener('input', (e) => {
            func(e);
        });

        return number_input;
    }

    //#region [UI]
    UI_Initialization(){

        // --- [UI container] ---
        const ui_container = document.createElement('div');
        ui_container.className = 'rm-ui-container';
        document.body.appendChild(ui_container);

        // avoid mouse event penetrate the UI elements
        ['mousedown', 'mousemove', 'mouseup', 'click', 'touchstart', 'touchend'].forEach(event => {
             ui_container.addEventListener(event, e => e.stopPropagation());
        });

        // --- [switch button] switch between line, settign and manual pages
        const switch_button_container = document.createElement('div');
        switch_button_container.className = 'rm-switch-button';
        ui_container.appendChild(switch_button_container);

        const switch_slider = document.createElement('div'); 
        switch_slider.className = 'rm-switch-slider';
        switch_button_container.appendChild(switch_slider);

        // --- [line page] (default)
        const line_page_container = document.createElement('div');
        line_page_container.className = 'rm-page-container';
        ui_container.appendChild(line_page_container);
        line_page_container.style.display = 'flex';

        // --- create draw lines container
        const drawlines_container = document.createElement('div');
        drawlines_container.className = 'rm-line-container';
        line_page_container.appendChild(drawlines_container);

        // --- create button container for add and delete buttons
        const button_container = document.createElement('div');
        button_container.className = 'rm-line-button-container';
        line_page_container.appendChild(button_container);

        // --- create add button
        const add_button = document.createElement('button');
        add_button.textContent = 'Add';
        add_button.className = 'rm-line-button rm-line-button-add';
        add_button.addEventListener('click', () => {
            // create new draw line
            this.AddDrawLine(new Vec3(0,0,0), new Vec3(0,1,0));
            this.UI_CreateLines();
        });
        button_container.appendChild(add_button);

        // --- create delete button
        const delete_button = document.createElement('button');
        delete_button.textContent = 'Delete';
        delete_button.className = 'rm-line-button rm-line-button-del';
        delete_button.addEventListener('click', () => {
            // remove objects of drawline
            this.DeleteSelectedDrawLine();
        });
        button_container.appendChild(delete_button);
        
        // --- [setting]
        const setting_page_container = document.createElement('div');
        setting_page_container.className = 'rm-page-container';
        ui_container.appendChild(setting_page_container);
        setting_page_container.style.display = 'none';
        
        // --- draw/edit switching
        this.IsDrawOnReferenceEntity = true; // default for first element
        const enable_draw_on_entity= () => {
            this.IsDrawOnReferenceEntity = true;
        }
        const disable_draw_on_entity= () => {
            this.IsDrawOnReferenceEntity = false;
        }
        this.UI_AddMultiSwitch(setting_page_container, "Mode", ["Draw", "Edit"], [enable_draw_on_entity, disable_draw_on_entity]);

        // --- enable/disable reference box
        const enable_reference_box = () => {
            this.DrawLineCube.enabled = true;
            this.App.renderNextFrame = true;
        }
        const disable_reference_box = () => {
            this.DrawLineCube.enabled = false;
            this.App.renderNextFrame = true;
        }
        this.UI_AddMultiSwitch(setting_page_container, "Reference Box (Appearance)", ["Show", "Hidden"], [enable_reference_box, disable_reference_box]);
        
        // --- fix/edit reference box
        const fix_reference_box = () => {
            this.DetachGizmos();
            this.App.renderNextFrame = true;
        }
        const translate_reference_box = () => {
            this.DetachGizmos();
            this.GizmoTranslateRef.attach(this.DrawLineCube);
            this.App.renderNextFrame = true;
        }
        const rotate_reference_box = () => {
            this.DetachGizmos();
            this.GizmoRotateRef.attach(this.DrawLineCube);
            this.App.renderNextFrame = true;
        }
        const scale_reference_box = () => {
            this.DetachGizmos();
            this.GizmoScaleRef.attach(this.DrawLineCube);
            this.App.renderNextFrame = true;
        }
        this.UI_AddMultiSwitch(setting_page_container, "Reference Box (Edit)", ["Fix", "Trans.", "Rot.", "Scale"], [fix_reference_box, translate_reference_box, rotate_reference_box, scale_reference_box]);

        // --- rescale/setlength switching
        this.IsReScale = true; // default for first element
        const rescale_on_label = () => {
            this.IsReScale = true;
        }
        const setlength_on_label= () => {
            this.IsReScale = false;
        }
        this.UI_AddMultiSwitch(setting_page_container, "Label Input", ["ReScale", "SetLength"], [rescale_on_label, setlength_on_label]);

        // --- unit mode selection
        const unit_modes: Array<() => void> = [];
        const unit_names: string[] = ['auto', 'mm', 'cm', 'm', 'km'];
        for(let i = 0; i < unit_names.length; i++){
            const unit_mode = () => {
                this.UnitMode = i;
                for(let i = 0; i < this.MarkingLines.length; i++){
                    this.UpdateLineLabel(this.MarkingLines[i]);
                }
            }
            unit_modes.push(unit_mode);
        }
        this.UI_AddMultiSwitch(setting_page_container, "Unit", unit_names, unit_modes);

        // --- create entity scale input field
        const update_entity_scale = (e: Event) => {
            const target = e.target as HTMLInputElement;
            const new_value = parseFloat(target.value);

            if (!isNaN(new_value) && new_value >= 0.01) {
                this.EntityScale = new_value;
                for(let i = 0; i < this.MarkingLines.length; i++){
                    this.MarkingLines[i].SetScale(this.EntityScale);
                }
                this.App.renderNextFrame = true;
            } 
            else {
                target.value = this.EntityScale.toString();
            }
        }
        this.SimpleInputField(setting_page_container, "Entity Scale", update_entity_scale, this.EntityScale);

        // --- create scale factor input field
        const update_scale = (e: Event) => {
            const target = e.target as HTMLInputElement;
            const new_value = parseFloat(target.value);

            if (!isNaN(new_value) && new_value >= 0.01) {
                this.ScaleFactor = new_value;
                for(let i = 0; i < this.MarkingLines.length; i++){
                    this.UpdateLineLabel(this.MarkingLines[i]);
                }
            } 
            else {
                target.value = this.ScaleFactor.toString();
            }
        }
        this.UIScaleInput = this.SimpleInputField(setting_page_container, "Scale Factor", update_scale, this.ScaleFactor);

        // --- create sensitivity input field
        const update_sensitivity = (e: Event) => {
            const target = e.target as HTMLInputElement;
            const new_value = parseFloat(target.value);
            if (!isNaN(new_value) && new_value >= 0.01) {
                this.Sensitivity = new_value;
            } 
            else {
                target.value = this.Sensitivity.toString();
            }
        }
        this.SimpleInputField(setting_page_container, "Sensitivity", update_sensitivity, this.Sensitivity);

        // --- get URL
        // create url container
        const url_container = document.createElement('div');
        url_container.className = 'rm-url-button-container';
        setting_page_container.appendChild(url_container);

        // create title
        const url_title_span = document.createElement('span');
        url_title_span.className = 'rm-url-button-label';
        url_title_span.textContent = "Output";
        url_container.appendChild(url_title_span);

        // create button
        const url_button = document.createElement('button');
        url_button.className = 'rm-url-button';
        url_button.textContent = 'Get URL';
        url_container.appendChild(url_button);
        url_button.addEventListener('click', ()=> {
            this.ShowLinkDrawLines();
        });

        // --- [manual page]
        const manual_page_container = document.createElement('div');
        manual_page_container.className = 'rm-page-container';
        ui_container.appendChild(manual_page_container);
        manual_page_container.style.display = 'none';

        const manual_container = document.createElement('div');
        manual_container.className = 'rm-line-container';
        manual_container.innerHTML = `
            <h3>尺規功能說明</h3>
            <li><b>新增線段</b>
                <li>1. 選取Draw模式，點選在參考Box上。</li>
                <li>2. 使用UI新增按鈕。</li>
            </li>
            <li><b>編輯端點</b>
                <li>1. 選取Edit模式，點選欲編輯之頂點後根據三軸箭頭拖動。</li>
                <li>2. 使用UI選取線段後設定座標。</li>
            </li>
            <li><b>刪除線段</b>
                <li>選取線段後使用UI刪除按鈕。</li>
            </li>
            <li<b>設定Scale</b>
                <li>選取ReScale點選線段標籤，輸入目標長度與單位。</li>
            </li>
            <li><b>設定線段長度</b>
                <li>選取SetLength點選線段標籤，輸入目標長度與單位。</li>
            </li>
            <li><b>設定參考Box</b>
                <li>選取Trans.、Rot.或Scale，根據三軸箭頭圓圈，可調整位置、旋轉與大小。</li>
            </li>
            <li><b>獲取包含線段的URL</b>
                <li>開啟設定頁面點擊Get URL。</li>
            </li>
        `;
        manual_page_container.appendChild(manual_container);
        // ------------------------------------

        // --- page switching through switch button
        const switch_span_line = document.createElement('span');
        switch_span_line.className = 'rm-switch-span rm-switch-span-a';
        switch_span_line.textContent = 'Lines';
        switch_button_container.appendChild(switch_span_line);
        switch_span_line.addEventListener('click', () => {
            switch_slider.style.left = '2.5%';
            line_page_container.style.display = 'flex';
            setting_page_container.style.display = 'none';
            manual_page_container.style.display = 'none';
        });
    
        const switch_span_setting = document.createElement('span');
        switch_span_setting.className = 'rm-switch-span rm-switch-span-b';
        switch_span_setting.textContent = 'Setting';
        switch_button_container.appendChild(switch_span_setting);
        switch_span_setting.addEventListener('click', () => {
            switch_slider.style.left = '35%';
            line_page_container.style.display = 'none';
            setting_page_container.style.display = 'flex';
            manual_page_container.style.display = 'none';
        });

        const switch_span_manual = document.createElement('span');
        switch_span_manual.className = 'rm-switch-span rm-switch-span-c';
        switch_span_manual.textContent = 'Manual';
        switch_button_container.appendChild(switch_span_manual);
        switch_span_manual.addEventListener('click', () => {
            switch_slider.style.left = '67.5%';
            line_page_container.style.display = 'none';
            setting_page_container.style.display = 'none';
            manual_page_container.style.display = 'flex';
        });

        return {ui_container, drawlines_container};
    }

    UI_CreateLines() {

        if(this.EnableOldMode) {
            this.UIDrawLinesContainer.innerHTML = '畫線功能不支援舊版URL，請清除URL中舊版data={...}、scale=...等參數後再進行線段編輯';
            return;
        }

        // reset draw lines container
        this.UIDrawLinesContainer.innerHTML = '';

        this.MarkingLines.forEach(line => {

            const line_container = document.createElement('div');
            line_container.className = 'rm-line-pos-container';
            line_container.style.backgroundColor = this.CurrentSelectedDrawLine === line ? '#FF8C00' : '#444444';
            line_container.addEventListener('click', (event) => {
                this.SelectDrawLine(line);
                event.stopPropagation();
            });

            // --- start position
            const start_position_label = document.createElement('div');
            start_position_label.className = 'rm-line-pos';
            start_position_label.textContent = 'Start Position:';
            start_position_label.style.color = this.CurrentSelectedDrawLine === line ? '#00FF00' : '#FFFFFF';
            start_position_label.style.borderRadius = this.CurrentSelectedDrawLine === line ? '5px' : '2px';
            start_position_label.style.pointerEvents = this.CurrentSelectedDrawLine === line ? 'auto' : 'none';
            start_position_label.appendChild(this.UI_CreatePositionInput(line.StartPosition, line));
            line_container.appendChild(start_position_label);
            
            if(line.EndPosition){
                // --- end position
                const end_position_label = document.createElement('div');
                end_position_label.className = 'rm-line-pos';
                end_position_label.textContent = 'End Position:';
                end_position_label.style.color = this.CurrentSelectedDrawLine === line ? '#00FFFF' : '#FFFFFF';
                end_position_label.style.borderRadius = this.CurrentSelectedDrawLine === line ? '5px' : '2px';
                end_position_label.style.pointerEvents = this.CurrentSelectedDrawLine === line ? 'auto' : 'none';
                end_position_label.appendChild(this.UI_CreatePositionInput(line.EndPosition, line));
                line_container.appendChild(end_position_label);
            }

            this.UIDrawLinesContainer.appendChild(line_container);
        });
    }

    UI_CreatePositionInput(position : Vec3, line: LineComponent, onChangeCallback = () => {}) {
        
        // --- create label for position
        const position_label = document.createElement('div');
        position_label.className = 'rm-pos-label';

        ['x', 'y', 'z'].forEach((axis: 'x'|'y'|'z') => {

            const position_input_wrapper = document.createElement('div');
            position_input_wrapper.className = 'rm-position-input-wrapper';

            // --- create axis label
            const axis_label = document.createElement('span');
            axis_label.className = 'rm-axis-label';
            axis_label.textContent = axis.toUpperCase() + ':';
            
            const hover_pointer = document.createElement('span');
            hover_pointer.className = 'rm-hover-pointer';
            hover_pointer.textContent = '<->';
            axis_label.addEventListener('mouseenter', () => {
                hover_pointer.style.opacity = '1';
            });
            axis_label.addEventListener('mouseleave', () => {
                hover_pointer.style.opacity = '0';
            });
            axis_label.appendChild(hover_pointer);

            // dragging events
            let is_dragging_input = false;
            let mouse_start_x = 0;
            let mouse_end_x = 0;
            let mouse_cumulative_displacement_x = 0;

            // mouse down for start dragging
            axis_label.addEventListener('mousedown', (e) => {
                
                is_dragging_input = true;
                this.IsDraggingActive = false;

                mouse_start_x = e.clientX;
                mouse_end_x = parseFloat(input.value);

                // request for pointer lock
                this.RequestPointerLock(document.body);

                // listener for track movement of the mouse
                document.addEventListener('mousemove', onMouseMove);

                // listener for release
                document.addEventListener('mouseup', onMouseUp);

                // listener to avoid selecting the words when dragging
                document.addEventListener('selectstart', this.DisableTextSelection);

                // set cursor 
                document.body.style.cursor = 'e-resize';
            });

            // --- listener for track movement of the mouse
            const onMouseMove = (e: MouseEvent) => {

                // dragging only one item
                if (!is_dragging_input) return;

                let mouse_delta_x;
                if (this.IsPointerLocked) {
                    mouse_delta_x = e.movementX;
                } 
                else {
                    mouse_delta_x = e.clientX - mouse_start_x;
                    mouse_start_x = e.clientX;
                }

                mouse_cumulative_displacement_x += mouse_delta_x;

                // update value if cumulative displacement of mouse over the threshold
                if (Math.abs(mouse_cumulative_displacement_x) >= this.MouseDisplaceCriteria) {
                    // const change_rate = Math.round(mouse_cumulative_displacement_x / this.Sensitivity);
                    // let new_value = mouse_end_x + change_rate;

                    let new_value = mouse_end_x + Math.sign(mouse_cumulative_displacement_x) * this.Sensitivity;
                    
                    input.value = new_value.toString();
                    position[axis] = new_value;
                    line.UpdateDrawLine();
                    this.UpdateLineLabel(line);
                    mouse_end_x = new_value;
                    mouse_cumulative_displacement_x = mouse_cumulative_displacement_x % this.Sensitivity;
                    
                    onChangeCallback();
                }
            };

            // --- listener for release
            const onMouseUp = () => {

                is_dragging_input = false;
                this.IsDraggingActive = false;

                // disable pointer lock
                if (this.IsPointerLocked) this.DisablePointerLock();

                // remove listeners for dragging
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                document.removeEventListener('selectstart', this.DisableTextSelection);

                // reset cursor
                document.body.style.cursor = 'default';

                // reset cumulative displacement
                mouse_cumulative_displacement_x = 0;
            };           

            // --- create input field
            const input = document.createElement('input');
            input.className = 'rm-pos-input';
            input.type = 'text';
            
            input.value = position[axis].toString();

            input.addEventListener('change', (e) => {

                const target = e.target as HTMLInputElement;
                const parsed_value = parseFloat(target.value);
                if (!isNaN(parsed_value)) {
                    position[axis] = parsed_value;
                    line.UpdateDrawLine();
                    this.UpdateLineLabel(line);
                    mouse_end_x = parsed_value;
                    onChangeCallback();
                }
                else{
                    input.value = position[axis].toString();
                    mouse_end_x = position[axis];
                }
            });

            position_input_wrapper.appendChild(axis_label);
            position_input_wrapper.appendChild(input);
            position_label.appendChild(position_input_wrapper);
        });

        return position_label;
    }

    // --- listener to avoid selecting the words when dragging
    DisableTextSelection = (e: MouseEvent) => {
        e.preventDefault();
    };

    PointerLockChangeHandler() {
        const customDocument = document as RulerMarkingDocument;

        if (customDocument.pointerLockElement === customDocument.body ||
            customDocument.mozPointerLockElement === customDocument.body ||
            customDocument.webkitPointerLockElement === customDocument.body) {
            
            this.IsPointerLocked = true;
        } else {
            this.IsPointerLocked = false;
            if (this.IsDraggingActive) {
                this.IsDraggingActive = false;
                customDocument.body.style.cursor = 'default';
                // Remove event listeners, casting the function types to match.
                customDocument.removeEventListener('mousemove', onMouseMove);
                customDocument.removeEventListener('mouseup', onMouseUp);
                customDocument.removeEventListener('selectstart', this.DisableTextSelection as EventListenerOrEventListenerObject);
            }
        }
    }

    RequestPointerLock(element: HTMLElement) {
        const customElement = element as RulerMarkingElement;

        if (customElement.requestPointerLock) {
            customElement.requestPointerLock();
        } else if (customElement.mozRequestPointerLock) {
            customElement.mozRequestPointerLock();
        } else if (customElement.webkitRequestPointerLock) {
            customElement.webkitRequestPointerLock();
        }
    }

    DisablePointerLock() {
        const customDocument = document as RulerMarkingDocument;

        if (customDocument.exitPointerLock) {
            customDocument.exitPointerLock();
        } else if (customDocument.mozExitPointerLock) {
            customDocument.mozExitPointerLock();
        } else if (customDocument.webkitExitPointerLock) {
            customDocument.webkitExitPointerLock();
        }
    }
    //#endregion

    //#region [Scale with Input Label]
    GetLabelValue(length: number){

        switch(this.UnitMode){

            case 0: // auto
                if (length / this.DictUnitFactors.get("km") > 1) {
                    return (length / this.DictUnitFactors.get("km")) + " km";
                } 
                else if (length / this.DictUnitFactors.get("m") > 1) {
                    return (length / this.DictUnitFactors.get("m")) + " m";
                } 
                else if (length / this.DictUnitFactors.get("cm") > 1){
                    return (length) + " cm";
                } 
                else {
                    return (length / this.DictUnitFactors.get("mm")) + " mm";
                }

            case 1: // mm
                return (length / this.DictUnitFactors.get("mm")) + " mm"; 

            case 2: // cm
                return (length) + " cm";

            case 3: // m
                return (length / this.DictUnitFactors.get("m")) + " m";

            case 4: // km
                return (length / this.DictUnitFactors.get("km")) + " km";

        }
    }

    GetLabelValueUI(length: number){

        switch(this.UnitMode){

            case 0: // auto
                if (length / this.DictUnitFactors.get("km") > 1) {
                    return (length / this.DictUnitFactors.get("km")).toFixed(2) + " km";
                } 
                else if (length / this.DictUnitFactors.get("m") > 1) {
                    return (length / this.DictUnitFactors.get("m")).toFixed(2) + " m";
                } 
                else if (length / this.DictUnitFactors.get("cm") > 1){
                    return (length).toFixed(2) + " cm";
                } 
                else {
                    return (length / this.DictUnitFactors.get("mm")).toFixed(2) + " mm";
                }

            case 1: // mm
                return (length / this.DictUnitFactors.get("mm")).toFixed(2) + " mm";

            case 2: // cm
                return (length).toFixed(2) + " cm";

            case 3: // m
                return (length / this.DictUnitFactors.get("m")).toFixed(2) + " m";

            case 4: // km
                return (length / this.DictUnitFactors.get("km")).toFixed(2) + " km";

        }
    }

    CreateLabel(length: number, marking_line: LineComponent) {
        
        // ---  HTML input field ---
        const label_entity = new LabelInput(this.App, this.App.root.findByName('camera') as Entity);
       
        label_entity.SetPlaceHolder(this.GetLabelValueUI(length)); 
        label_entity.InputField.setAttribute('holded-value', this.GetLabelValue(length));

        label_entity.InputField.addEventListener('focus', () => {
            // this.activeInputElement = input_label;
            label_entity.InputField.style.borderColor = 'blue';
            label_entity.SetPlaceHolder('');
        });

        label_entity.InputField.addEventListener('blur', () => {
            // this.activeInputElement = null;
            label_entity.InputField.style.borderColor = '#ccc';

            label_entity.InputField.style.background = 'rgba(254, 255, 186, 0.9)';

            if (label_entity.InputField.value.trim() !== ''){
                // convert input with unit to the centimeter
                const input_value = this.UnitConverter(label_entity.InputField.value)


                // based on cm
                if (input_value >= 0.001 && input_value <= 10000000){
                    
                    if(this.IsReScale){

                        const holded_value = this.UnitConverter(label_entity.InputField.getAttribute('holded-value'));


                        this.UpdateLineScale(input_value, holded_value);
                        label_entity.InputField.style.background = 'rgba(254, 255, 186, 0.9)';

                    }
                    else{

                        let new_length = input_value / this.ScaleFactor;

                        if (!isNaN(new_length)) {
                            this.EditDrawLineLength(marking_line, new_length);
                            // input_label.value = this.UnitConverter(marking_line.StartPosition.distanceTo(marking_line.EndPosition).toFixed(2));
                            this.UI_CreateLines();
                        }
                    }

                    label_entity.InputField.value = '';
                }
                else{
                    label_entity.InputField.style.background = 'rgba(255, 152, 105, 0.9)';
                    const holded_value = this.UnitConverter(label_entity.InputField.getAttribute('holded-value'));
                    label_entity.SetPlaceHolder(this.GetLabelValueUI(holded_value));
                }
                
            }
            else{
                const holded_value = this.UnitConverter(label_entity.InputField.getAttribute('holded-value'));
                label_entity.SetPlaceHolder(this.GetLabelValueUI(holded_value));
            }

        });        

        return label_entity;
    }

    UpdateLineScale(input_value: number, holded_value: number){

        this.ScaleFactor = this.ScaleFactor * (input_value / holded_value);

        this.UIScaleInput.value = this.ScaleFactor.toString();

        if(this.MarkingLines){
            for(let i = 0; i < this.MarkingLines.length; i++){
                this.UpdateLineLabel(this.MarkingLines[i]);
            }
        }
    }

    UpdateLineLabel(marking_line: LineComponent){

        if(this.EnableOldMode) return;

        if(!marking_line.isLineDone) return; 
        const new_length = this.ScaleFactor * marking_line.GetLength();
        const mid_point = marking_line.GetMidPoint();


        marking_line.UpdateInputLabel(mid_point, this.GetLabelValueUI(new_length), this.GetLabelValue(new_length));
    }

    EditDrawLineVertex(marking_line: LineComponent, edited_sphere: Entity, new_position: Vec3){
        
        if(this.EnableOldMode) return;

        // update position of selected sphere
        const target_posistion = marking_line.MapEntityToPosition.get(edited_sphere);
        target_posistion.set(new_position.x, new_position.y, new_position.z);
        // if (target_posistion) marking_line.ConstrainVectorPrecisions(target_posistion);

        // udpate draw line
        marking_line.UpdateDrawLine();

        // update line label
        this.UpdateLineLabel(marking_line);
    }

    EditDrawLineLength(marking_line: LineComponent, length: number){

        if(this.EnableOldMode) return;

        // set end position by length
        marking_line.SetEndPositionByLength(length);

        // udpate draw line
        marking_line.UpdateDrawLine();

        // update line label
        this.UpdateLineLabel(marking_line);
    }

    SetLineLabelConstant(marking_line: LineComponent, length: number){
        
        if(!marking_line.isLineDone) return;
        
        const mid_point = marking_line.GetMidPoint();

        marking_line.UpdateInputLabel(mid_point, this.GetLabelValueUI(length), this.GetLabelValue(length));
    }


    UnitConverter(input_string: string) {
        
        // clean up the input_string
        const clean_input_string = input_string.trim().replace(/\s+/g, ' ');

        // supported units string
        const supported_units = Array.from(this.DictUnitFactors.keys()).join('|');

        // ^ : begin of the string
        // (\\d+(\\.\\d+)?) : support for the integer and point
        // \\s* : 0 or more spaces
        // $ : end of the string
        // i : support for lowercase and uppercase
        const match_support_unit = clean_input_string.match(new RegExp(`^(\\d+(\\.\\d+)?)\\s*(${supported_units})$`, 'i'));

        // check the unit is km, m, cm, mm
        if (!match_support_unit) return -1;
        
        // obtain origin value
        const origin_value = parseFloat(match_support_unit[1]);

        // check the unit is positive
        if (origin_value <= 0) return -1;

        // obtain origin unit with lowercase
        const origin_unit = match_support_unit[3].toLowerCase();

        // return the value with target unit
        return origin_value * this.DictUnitFactors.get(origin_unit);
    }
    //#endregion
    

    //#region [Axis Arrows]
    CreateArrow(length: number, radius: number, color: Color): Entity {

        const material = new StandardMaterial();
        material.diffuse = color;
        material.emissive = color;
        material.useLighting = false;
        material.depthTest = true;
        material.depthWrite = true;
        material.blendType = BLEND_NONE;
        material.update();

        const cylinder = new Entity('cylinder');
        cylinder.addComponent('model', { type: 'cylinder' });
        cylinder.model.material = material;
        cylinder.setLocalScale(radius, length, radius); 
        cylinder.setLocalPosition(0, length / 2, 0);

        const cone = new Entity('cone');
        cone.addComponent('model', { type: 'cone' });
        cone.model.material = material;
        cone.setLocalScale(radius * 2, radius * 4, radius * 2);
        const coneBias = radius * 1.2;
        cone.setLocalPosition(0, length + coneBias, 0);

        const arrow = new Entity('arrow');
        arrow.addChild(cylinder);
        arrow.addChild(cone);

        cylinder.model.meshInstances.forEach(mi => mi.drawOrder = DrawOrderBasic + LastDrawOrderOffset);
        cone.model.meshInstances.forEach(mi => mi.drawOrder = DrawOrderBasic + LastDrawOrderOffset);
        LastDrawOrderOffset += 1;

        this.App.renderNextFrame = true;

        return arrow;
    }


    CreateAxisArrow(length: number, radius: number, color: Color, axis: 'x' | 'y' | 'z'): Entity {
        const arrow = this.CreateArrow(length, radius, color);
        arrow.name = axis;

        switch (axis) {
            case 'x':
                arrow.setLocalEulerAngles(0, 0, -90); // rotate 90 degrees on z-axis
                break;
            case 'y':
                // no need for rotation
                break;
            case 'z':
                arrow.setLocalEulerAngles(90, 0, 0); // rotate 90 degrees on x-axis
                break;
            default:

                break;
        }

        return arrow;
    }
    //#endregion

    //#region [Add New Draw Line]
    AddVertex(position: Vec3){

        if(this.EnableOldMode) return;

        if(this.MapSphereToDrawLine.size <= 0 || this.MarkingLines[this.MarkingLines.length - 1].IsDrawLineDone()){

            const new_draw_line = new LineComponent(position, null, this.App, this.EntityScale);
            this.MapSphereToDrawLine.set(new_draw_line.StartSphereEntity, new_draw_line);
            this.MarkingLines.push(new_draw_line);
        }
        else {
            const last_done_draw_line = this.MarkingLines[this.MarkingLines.length - 1];
            last_done_draw_line.SetEndPosition(position);
            last_done_draw_line.LineLabelEntity = this.CreateLabel(1, last_done_draw_line);
            last_done_draw_line.CreateDrawLine();
            this.UpdateLineLabel(last_done_draw_line);

            this.MapSphereToDrawLine.set(last_done_draw_line.EndSphereEntity, last_done_draw_line);
            this.MapCylinderToDrawLine.set(last_done_draw_line.LineCylinderEntity, last_done_draw_line);
        }

        this.UI_CreateLines();
    }

    AddDrawLine(start_position: Vec3, end_position: Vec3){

        // to keep start and end points are pair by pair
        if(this.MapSphereToDrawLine.size > 0 && !this.MarkingLines[this.MarkingLines.length - 1].IsDrawLineDone()){
            this.AddVertex(new Vec3(0,0,0));
        }

        const new_draw_line = new LineComponent(start_position, end_position, this.App, this.EntityScale);
        new_draw_line.LineLabelEntity = this.CreateLabel(1, new_draw_line);
        new_draw_line.CreateDrawLine();
        this.UpdateLineLabel(new_draw_line);

        this.MapSphereToDrawLine.set(new_draw_line.StartSphereEntity, new_draw_line);
        this.MapSphereToDrawLine.set(new_draw_line.EndSphereEntity, new_draw_line);
        this.MapCylinderToDrawLine.set(new_draw_line.LineCylinderEntity, new_draw_line);
        this.MarkingLines.push(new_draw_line);

        this.UI_CreateLines();
    }
    //#endregion

    //#region [Enable/Disable Draw Lines]
    EnableDrawLinesEdit(){
        this.UIContainer.style.display = 'flex';
        this.App.root.addChild(this.DrawLineCube);
        for (let i = 0; i < this.MarkingLines.length; i++) if(this.MarkingLines[i].LineLabelEntity) this.MarkingLines[i].LineLabelEntity.InputField.style.pointerEvents = 'auto';
        this.App.renderNextFrame = true;
    }

    DisableDrawLinesEdit(){
        this.UIContainer.style.display = 'none';
        this.App.root.removeChild(this.DrawLineCube);
        for (let i = 0; i < this.MarkingLines.length; i++) if(this.MarkingLines[i].LineLabelEntity) this.MarkingLines[i].LineLabelEntity.InputField.style.pointerEvents = 'none';
        this.App.renderNextFrame = true;
    }

    DisableDrawLines(){
        for (let i = 0; i < this.MarkingLines.length; i++) this.MarkingLines[i].RemoveObjectsFromScene();
        this.DisableDrawLinesEdit();
        this.App.renderNextFrame = true;
    }

    EnableDrawLines(){
        for (let i = 0; i < this.MarkingLines.length; i++) this.MarkingLines[i].AddObjectsToScene();
        this.App.renderNextFrame = true;
    }
    //#endregion

    //#region [Get MarkingLines from URL]
    GetParameterByName(name: string) {
        let current_url = new URL(window.location.href);
        const url_parameter = current_url.searchParams;

        return url_parameter.get(name);
    }

    RotateAroundTargetCenter(point: Vec3, target_pos: Vec3): Vec3 {

        const local = point.clone().sub(target_pos);
        const rotatedLocal = new Vec3();
        this.SpaceOuat.transformVector(local, rotatedLocal);

        return rotatedLocal.add(target_pos);
    }

    GetDrawLinesFromURL(){
        
        // deal with old URL
        const old_data_parameter = this.GetParameterByName('data');
        var old_data_json;
        if (old_data_parameter) {
            try {
                // decode and then parse the JSON
                old_data_json = JSON.parse(decodeURIComponent(old_data_parameter));
            } catch (e) {

                return;
            }
        } 

        if(old_data_json && Object.keys(old_data_json).length > 0){
            
            this.EnableOldMode = true;
            let line_count = 0;
            // append the lines from dls_json
            for (let line_key in old_data_json) {
                
                const start_pos = this.RotateAroundTargetCenter(new Vec3(-old_data_json[line_key][0][0], -old_data_json[line_key][0][1], old_data_json[line_key][0][2]), this.SpaceOuat.transformVector(this.GSplatEntityPosition));
                const end_pos = this.RotateAroundTargetCenter(new Vec3(-old_data_json[line_key][1][0], -old_data_json[line_key][1][1], old_data_json[line_key][1][2]), this.SpaceOuat.transformVector(this.GSplatEntityPosition));

                this.AddDrawLine(start_pos, end_pos);

                if(old_data_json[line_key][2] !== undefined){

                    this.SetLineLabelConstant(this.MarkingLines[this.MarkingLines.length - 1], parseFloat(old_data_json[line_key][2]));

                    if (line_count === 0) this.ScaleFactor = parseFloat(old_data_json[line_key][2]) / start_pos.distance(end_pos);
                }
                line_count++;
            }
            this.EntityScale = 0.05
            return;
        }

        // load draw lines from URL
        const dls_parameter = this.GetParameterByName('dls');

        var dls_json;
        if (dls_parameter) {
            try {
                // decode and then parse the JSON
                dls_json = JSON.parse(decodeURIComponent(dls_parameter));

            } catch (e) {

                return;
            }
        } else {

            return;
        }
        // set the unit mode for unit
        let url_unit = this.GetParameterByName('ut');
        this.UnitMode = (url_unit) ? parseInt(url_unit) : 0;

        // set the scale factor from sf
        let sf = this.GetParameterByName('sf');
        this.ScaleFactor = (sf) ? parseFloat(sf) : 1;

        // set the entity scale for line
        let es = this.GetParameterByName('es');
        this.EntityScale = (es) ? parseFloat(es) : this.EntityScale;


        // append the lines from dls_json
        let sf_first_line = null;
        let line_count = 0;
        for (let line_key in dls_json) {
            const start_pos = new Vec3(dls_json[line_key][0][0], dls_json[line_key][0][1], dls_json[line_key][0][2]);
            const end_pos = new Vec3(dls_json[line_key][1][0], dls_json[line_key][1][1], dls_json[line_key][1][2]);
            this.AddDrawLine(start_pos, end_pos);

            if(dls_json[line_key][2] !== undefined){

                let loaded_length = parseFloat(dls_json[line_key][2]) / this.ScaleFactor;
                this.EditDrawLineLength(this.MarkingLines[this.MarkingLines.length - 1], loaded_length);

                if (line_count === 0) sf_first_line = loaded_length / start_pos.distance(end_pos);
            }
            line_count++;
        }
    }
    //#endregion

    //#region [Show Link with Lines]
    ShowLinkDrawLines(){

        let current_url = new URL(window.location.href);
        const url_parameter = current_url.searchParams;

        url_parameter.delete('cp');
        url_parameter.delete('cla');
        url_parameter.delete('cu');
        url_parameter.delete('sf');
        url_parameter.delete('dls');
        url_parameter.delete('es');

        // set cp, cla and cu
        const camera_info = this.GetCameraInfo();
        const insert_params = new URLSearchParams(camera_info);
        insert_params.forEach((value, key) => {
            url_parameter.set(key, value);
        });

        // set unit
        if (this.UnitMode !== 0) url_parameter.set('ut', this.UnitMode.toString());

        // set scale factor
        url_parameter.set('sf', this.ScaleFactor.toString());

        // set entity scale
        url_parameter.set('es', this.EntityScale.toString());

        // set lines
        let lines_text = '{';
        for (let n = 0; n < this.MarkingLines.length; n++) {
            if (!this.MarkingLines[n].isLineDone) continue;
            lines_text += '"line' + (n+1) + '":['
            lines_text += '[' + this.MarkingLines[n].StartPosition.x.toFixed(2) + ',' + this.MarkingLines[n].StartPosition.y.toFixed(2) + ',' + this.MarkingLines[n].StartPosition.z.toFixed(2) + '],';
            lines_text += '[' + this.MarkingLines[n].EndPosition.x.toFixed(2) + ',' + this.MarkingLines[n].EndPosition.y.toFixed(2) + ',' + this.MarkingLines[n].EndPosition.z.toFixed(2) + '],';
            lines_text += '"' + (this.MarkingLines[n].StartPosition.distance(this.MarkingLines[n].EndPosition)) * this.ScaleFactor + '"]';
            if (n < this.MarkingLines.length - 1) lines_text += ',';
        }
        lines_text += '}';
        url_parameter.set('dls', lines_text);

        // show panel with novel URL
        const new_url = decodeURIComponent(current_url.toString())
        if (window.confirm("是否前往新網址: \n" + new_url)) {
            window.location.href = new_url;
        }
    }
    //#endregion
    
    //#region [Mouse Events]
    public MouseClickEvent(): void {

        let mouseDownTime = 0;
        let isDragging = false;
        let mouseDownPosition: Vec2 | null = null;

        this.App.mouse.on('mousedown', (event: MouseEvent) => {
            if (!this.IsEnableRulerMarkingEdit || event.button !== 0) return;

            mouseDownTime = performance.now();

            isDragging = false;
            mouseDownPosition = new Vec2(event.x, event.y);

            if(this.DrawLineCube){
                this.DrawLineCube.collision.halfExtents = this.DrawLineCube.getScale().clone().mulScalar(0.5);
                this.App.renderNextFrame = true;
            }
        });

        this.App.mouse.on('mousemove', (event: MouseEvent) => {

            if (mouseDownPosition) {
                const dx = event.x - mouseDownPosition.x;
                const dy = event.y - mouseDownPosition.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance > 2) {
                    isDragging = true;
                }
            }

        });

        this.App.mouse.on('mouseup', (event: MouseEvent) => {

            if (!this.IsEnableRulerMarkingEdit || event.button !== 0 || isDragging) return;

            if (mouseDownTime !== null) {

                const elapsed = performance.now() - mouseDownTime;
                mouseDownTime = 0;

                if (elapsed < 250) {
                    
                    const camera_entity = this.App.root.findByName('camera') as Entity;

                    if (!camera_entity || !camera_entity.camera) {
                        return;
                    }

                    const from = camera_entity.getPosition();

                    const to = camera_entity.camera.screenToWorld(
                        event.x, 
                        event.y, 
                        camera_entity.camera.farClip
                    );

                    this.ReleaseAllSelected();

                    // draw mode
                    if (this.IsDrawOnReferenceEntity){

                        // perform raycast
                        const outHits: RaycastResult[] = this.App.systems.rigidbody.raycastAll(from, to, {filterTags: [ "drawref"]});
                        
                        if (outHits.length > 0){
                            this.AddVertex(outHits[0].point);
                        } 
                    }
                    // edit mode (select sphere)
                    else{

                        this.ReleaseSelectedLine();
                        this.ReleaseSelectedSphere();
                        this.DetachGizmos();

                        // perform raycast
                        const outHits: RaycastResult[] = this.App.systems.rigidbody.raycastAll(from, to, {filterTags: [ "endpoint"]});            
                        
                        if(outHits.length > 0){


                            // select cylinder
                            if(this.MapCylinderToDrawLine.has(outHits[0].entity)){
                                let hit_drawline = this.MapCylinderToDrawLine.get(outHits[0].entity);
                                if (hit_drawline && this.CurrentSelectedDrawLine != hit_drawline) this.SelectDrawLine(hit_drawline);
                            }
                            // select sphere (editable)
                            else if (this.MapSphereToDrawLine.has(outHits[0].entity)){

                                this.CurrentSelectedSphere = outHits[0].entity;

                                const material = this.CurrentSelectedSphere.model.material as StandardMaterial;
                                material.diffuse = this.ColorSelectedPoint;
                                material.emissive = this.ColorSelectedPoint;
                                material.blendType = BLEND_NONE;
                                material.depthTest = true;
                                material.depthWrite = true;
                                material.update();

                                this.GizmoTranslate.attach(this.CurrentSelectedSphere);


                                // this.UpdateDragAxes(this.CurrentSelectedSphere);
                            }
                        }
                    }

                    this.App.renderNextFrame = true;
                }
            }
        });



        this.App.mouse.on('mousedown', function (event: MouseEvent) {

            if (!this.IsEnableRulerMarkingEdit || event.button !== 0) return;

            

        }, this);
        
    }
    //#endregion

    //#region [Draw Line Selecting and Releasing]
    SelectDrawLine(marking_line: LineComponent){
        if(this.CurrentSelectedDrawLine === marking_line) return;
        if(this.CurrentSelectedDrawLine){
            this.CurrentSelectedDrawLine.SetColor(this.ColorDefault, this.ColorDefault, this.ColorDefault);
            if(this.CurrentSelectedDrawLine.LineLabelEntity) this.CurrentSelectedDrawLine.LineLabelEntity.InputField.style.border = '1px solid #cccccc';
        } 
        this.CurrentSelectedDrawLine = marking_line;
        this.CurrentSelectedDrawLine.SetColor(Color.GREEN, Color.RED, Color.CYAN);
        if(this.CurrentSelectedDrawLine.LineLabelEntity) this.CurrentSelectedDrawLine.LineLabelEntity.InputField.style.border = '3px solid #FF8C00';
        this.UI_CreateLines();
        this.App.renderNextFrame = true;
    }

    ReleaseSelectedLine(){
        if(this.CurrentSelectedDrawLine){
            this.CurrentSelectedDrawLine.SetColor(this.ColorDefault, this.ColorDefault, this.ColorDefault);
            if(this.CurrentSelectedDrawLine.LineLabelEntity) this.CurrentSelectedDrawLine.LineLabelEntity.InputField.style.border = '1px solid #cccccc';
        } 
        this.CurrentSelectedDrawLine = null;
        this.UI_CreateLines();
        this.App.renderNextFrame = true;
    }

    ReleaseSelectedSphere(){
        if(this.CurrentSelectedSphere){
            const material = this.CurrentSelectedSphere.model.material as StandardMaterial;
            material.diffuse = this.ColorDefault;
            material.emissive = this.ColorDefault;
            material.blendType = BLEND_NONE;
            material.depthTest = true;
            material.depthWrite = true;
            material.update();
        }
        this.CurrentSelectedSphere = null;
    }

    ReleaseAllSelected(){
        this.ReleaseSelectedSphere();
        this.ReleaseSelectedLine();
    }
    //#endregion

    //#region [Draw Line Deleting]
    DeleteSelectedDrawLine(){
        if(this.CurrentSelectedDrawLine){
            if(this.CurrentSelectedSphere && this.CurrentSelectedDrawLine.MapEntityToPosition.has(this.CurrentSelectedSphere)){
                this.CurrentSelectedSphere = null;
            } 
            this.CurrentSelectedDrawLine.Destroy();
            this.RemoveMapItems(this.MapSphereToDrawLine, this.CurrentSelectedDrawLine);
            this.RemoveMapItems(this.MapCylinderToDrawLine, this.CurrentSelectedDrawLine);
            this.RemoveObjectFromArray(this.MarkingLines, this.CurrentSelectedDrawLine);
            this.CurrentSelectedDrawLine = null;
        }
        this.UI_CreateLines();
        this.App.renderNextFrame = true;
    }

    RemoveMapItems(target_map: Map<Entity, LineComponent>, target_line: LineComponent) {
        target_map.forEach((value, key) => {
            if (value === target_line) target_map.delete(key);
        });
    }

    RemoveObjectFromArray(arr: LineComponent[], target_line: LineComponent) {
        const index = arr.indexOf(target_line);
        if (index > -1) arr.splice(index, 1);
    }
    //#endregion
    

    Debug(){
    }
}

export {RulerMarking};